<template>
  <div class="orders">

    <!-- Header -->
    <div class="orders-line" :class="{'hidden-mobile': getOrderedLocations.length <= 1}">
      <h3 class="orders-line-title">{{ $t('My orders') }}</h3>
      <order-history-select-location class="orders-line-listbox"
                                     @switchLocation="switchLocation"
                                     :locations="getOrderedLocations"
                                     v-if="getOrderedLocations.length > 1"
      />
    </div>
    <!---->

    <!-- Orders -->
    <div class="orders-block">
      <template v-if="ordersLoader">
        <order-skeleton v-for="index in 5" :key="index" />
      </template>
      <template v-else>
        <div class="orders-block-list" v-show="!isHistoryEmpty">
          <div infinite-wrapper>
            <order class="orders-block-list-item"
                   v-for="order in ordersHistory"
                   :key="order.id"
                   :order="order"
            />
          </div>
          <pay-order-modal v-if="!orderForPay.is_paid" :order="orderForPay"/>
          <infinite-loading ref="orders-infinite" force-use-infinite-wrapper :distance=50 @infinite="loadOrders">
            <span slot="no-more" />
            <span slot="error" />
            <span slot="no-results" />
            <span slot="spinner" />
          </infinite-loading>
        </div>
        <div class="col-xs-12 h4" v-show="isHistoryEmpty && !loader">
          <p>{{ $t('No orders yet') }}</p>
        </div>
      </template>
    </div>
    <!---->

  </div>
</template>

<script async>
import UserOrder from 'src/modules/order-history/components/UserOrders'
import { mapGetters, mapActions } from 'vuex';
import InfiniteLoading from 'vue-infinite-loading'

const PayOrderModal = () => import(/* webpackChunkName: "vsf-pay-order-modal" */ 'theme/components/core/blocks/MyAccount/Order/PayOrderModal');
const OrderHistorySelectLocation = () => import(/* webpackChunkName: "vsf-order-history-select-location" */ 'theme/components/core/blocks/MyAccount/OrderHistorySelectLocation');
const Order = () => import(/* webpackChunkName: "vsf-order" */ 'theme/components/core/blocks/MyAccount/Order');
const OrderSkeleton = () => import(/* webpackChunkName: "vsf-order-skeleton" */ 'theme/components/core/blocks/MyAccount/Order/OrderSkeleton');
import { isServer } from '@vue-storefront/core/helpers';

export default {
  mixins: [UserOrder],
  components: {
    PayOrderModal,
    Order,
    OrderHistorySelectLocation,
    InfiniteLoading: isServer ? { render: h => h() } : InfiniteLoading,
    OrderSkeleton
  },
  data () {
    return {
      ordersLoader: true,
      orderId: 0,
      products: {},
      count: 0
    }
  },
  async mounted () {
    let currentLocation = '';
    let locations = await this.orderedLocationsAction()
    if (locations && locations.length) {
      currentLocation = locations.find(location => this.locationActive && (this.locationActive.code === location))
      if (!currentLocation) {
        currentLocation = locations[0]
        await this.changeLocation({code: currentLocation}).catch(error => {
          console.error(error)
        })
      }
      if (this.ordersHistory && this.ordersHistory.length && this.ordersHistory[0].location_code !== currentLocation) {
        this.clearOrderHistory();
      }
      await this.ordersHistoryAction({
        refresh: true,
        useCache: false,
        oldLocationCode: currentLocation,
        locationCode: currentLocation
      }).finally(() => {
        this.ordersLoader = false
      })
      if (this.$route.query.pay_for) {
        if (this.isOrderLoaded) {
          this.orderForPay = this.isOrderLoaded
          if (this.needToPay) {
            this.pay()
          }
        } else {
          this.$store.commit('ui/setLoader', true)
          this.getOneOrder(this.$route.query.pay_for).then(result => {
            this.orderForPay = result
          })
            .then(() => {
              if (this.needToPay) {
                this.pay()
              }
            }).finally(() => {
              this.$store.commit('ui/setLoader', false)
            })
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      loader: 'ui/getLoader',
      locationByCode: 'locations/getLocationByCode',
      locationActive: 'locations/getActiveLocation'
    }),
    isOrderLoaded () {
      return this.ordersHistory.find(order => order.id === parseInt(this.$route.query.pay_for))
    },
    needToPay () {
      return this.orderForPay['id'] &&
        ['ipay_masterpass', 'ipay_applepay', 'ipay_gpay'].includes(this.orderForPay.payment_method_code) && !this.orderForPay.is_paid
    }
  },
  methods: {
    ...mapActions({
      'ordersHistoryAction': 'user/getOrdersHistory',
      orderedLocationsAction: 'user/orderedLocations',
      clearOrderHistory: 'user/clearOrderHistory',
      changeLocation: 'locations/setCurrentLocation',
      loadPartners: 'partners/loadPartners'
    }),
    async pay () {
      this.$bus.$emit('modal-show', `pay-order-${this.orderForPay.id}`)
    },
    async switchLocation (locationCode) {
      this.ordersLoader = true;
      this.clearOrderHistory();
      this.pagination.current = 1;
      let oldLocationCode = this.locationActive.code
      await this.changeLocation({ code: locationCode }).catch(error => {
        console.error(error)
      })
      this.ordersHistoryAction({ refresh: true, useCache: false, oldLocationCode: oldLocationCode, locationCode: locationCode }).finally(() => {
        this.ordersLoader = false
        this.$nextTick(() => {
          const infinite = this.$refs['orders-infinite']
          if (infinite) infinite.stateChanger.reset()
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.orders {
  @media only screen and (max-width: 576px) {
    padding: 0 20px;
  }

  &-line {
    height: 60px;
    flex: 1 0 60px;
    margin-bottom: 20px;

    display: flex;
    justify-content: space-between;
    align-items: center;
    &.hidden-mobile {
      @media (max-width: 576px) {
        display: none;
      }
    }

    &-listbox {
      width: 300px;

      @media only screen and (max-width: 768px) {
        width: 200px;
      }

      @media only screen and (max-width: 576px) {
        flex: 1 auto;
      }
    }

    &-btn {
      width: 40px;
      height: 40px;

      background-color: #11bb2e;
      border: none;
      border-radius: 5px;

      background-image: url('../../../../assets/account/icon-calendar.png');
      background-position: center;
      background-size: 20px;
      background-repeat: no-repeat;

      user-select: none;
      outline: none;
    }
    &-title {
      margin: 0;
      color: #111;
      font-size: 20px;
      font-weight: 600;
      @media (max-width: 576px) {
        display: none;
      }
    }
  }

  &-block {
    overflow: auto;
    overflow-x: hidden;

    &-list {
      display: flex;
      flex-direction: column;
    }
  }
}
</style>
